import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FoodItem,
  FoodItemAndEquivalentFoods,
  FoodItemAndUsages,
  FoodItemAndVariantFoods,
  FoodItemBasic,
  FoodItemNutrition,
  FoodItemReturnType,
  FoodItemSupplierInfo,
  FoodItemTag,
  FoodRecipe,
  FoodTagCategory,
  FoodUsageAsIngredient,
  MenuLoopUsage,
  MenuUsage,
} from "client/jspPlatformExperiment";
import isEmpty from "lodash-es/isEmpty";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";

interface FoodItemDetailStateProps {
  /** Return value of food item data from the Jasper Platform API call */
  foodItemFull: FoodItem | null;
  foodItem: FoodItemBasic | null;
  foodTags: FoodItemTag[];
  foodNutritionRecords: FoodItemNutrition[];
  foodRecipes: FoodRecipe[];
  foodSuppliers: FoodItemSupplierInfo[];
  /** Populated when getFoodItemByFoodId is called with return_type "food and equivalent foods" */
  equivalentFoodItems: FoodItem[];
  /** Populated when getFoodItemByFoodId is called with return_type "food and usages" */
  usedAsIngredientIn: FoodUsageAsIngredient[];
  usedInMenus: MenuUsage[];
  usedInMenuLoops: MenuLoopUsage[];
  /** Populated when getFoodItemByFoodId is called with return_type "food and variant foods" */
  variantFoodItems: FoodItem[];
  isLoading: boolean;
  isApprovalChanging: boolean;
  error: string | null;
}

const initialState: FoodItemDetailStateProps = {
  foodItemFull: null,
  foodItem: null,
  foodTags: [
    { id: 17, tag_categories: [FoodTagCategory.DIET_TYPE] },
    { id: 18, tag_categories: [FoodTagCategory.DIET_TYPE] },
    { id: 19, tag_categories: [FoodTagCategory.DIET_TYPE] },
    { id: 64, tag_categories: [FoodTagCategory.DIET_TYPE] },
    { id: 87, tag_categories: [FoodTagCategory.DIET_TYPE] },
    { id: 89, tag_categories: [FoodTagCategory.DIET_TYPE] },
    { id: 80, tag_categories: [FoodTagCategory.DIET_TYPE] },
    { id: 365, tag_categories: [FoodTagCategory.DIET_TYPE] },
    { id: 63, tag_categories: [FoodTagCategory.FOOD_TEXTURE] },
  ],
  foodNutritionRecords: [],
  foodRecipes: [],
  foodSuppliers: [],
  equivalentFoodItems: [],
  usedAsIngredientIn: [],
  usedInMenus: [],
  usedInMenuLoops: [],
  variantFoodItems: [],
  isLoading: false,
  isApprovalChanging: false,
  error: null,
};

const fetchFoodItemDetail = createAsyncThunk(
  "foodItemDetail/fetchFoodItemDetail",
  async (input: { foodId: number }, { rejectWithValue }) => {
    try {
      const res = await jspPlatformApi().foodItem.getFoodItemByFoodId({
        path: { food_id: input.foodId },
        query: { return_type: FoodItemReturnType.FOOD_AND_EQUIVALENT_FOODS },
      });
      return (res.data ?? null) as FoodItemAndEquivalentFoods | null;
    } catch (error) {
      let errMsg: string;
      const err = isApiError(error) ? toJspApiError(error) : null;
      if (err && isErrorResponseBodyType(err.body)) {
        errMsg = err.body.detail;
      } else {
        errMsg = "Unknown error!";
      }
      return rejectWithValue(errMsg);
    }
  },
);

const fetchFoodItemUsages = createAsyncThunk(
  "foodItemDetail/fetchFoodItemUsages",
  async (input: { foodId: number }, { rejectWithValue }) => {
    try {
      const res = await jspPlatformApi().foodItem.getFoodItemByFoodId({
        path: { food_id: input.foodId },
        query: { return_type: FoodItemReturnType.FOOD_AND_USAGES },
      });
      return (res.data ?? null) as FoodItemAndUsages | null;
    } catch (error) {
      let errMsg: string;
      const err = isApiError(error) ? toJspApiError(error) : null;
      if (err && isErrorResponseBodyType(err.body)) {
        errMsg = err.body.detail;
      } else {
        errMsg = "Unknown error!";
      }
      return rejectWithValue(errMsg);
    }
  },
);

const fetchFoodItemAndVariants = createAsyncThunk(
  "foodItemDetail/fetchFoodItemAndVariants",
  async (input: { foodId: number }, { rejectWithValue }) => {
    try {
      const res = await jspPlatformApi().foodItem.getFoodItemByFoodId({
        path: { food_id: input.foodId },
        query: { return_type: FoodItemReturnType.FOOD_AND_VARIANT_FOODS },
      });
      return (res.data ?? null) as FoodItemAndVariantFoods | null;
    } catch (error) {
      let errMsg: string;
      const err = isApiError(error) ? toJspApiError(error) : null;
      if (err && isErrorResponseBodyType(err.body)) {
        errMsg = err.body.detail;
      } else {
        errMsg = "Unknown error!";
      }
      return rejectWithValue(errMsg);
    }
  },
);

const updateFoodItemApprovalState = createAsyncThunk(
  "foodItemDetail/updateFoodItemApprovalState",
  async (
    input: {
      foodId: number;
      body?: Array<1 | 6 | 7> | null;
      disapproval?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await jspPlatformApi().foodItem.postFoodItemApprovalByFoodId({
        path: { food_id: input.foodId },
        body: input.body,
        query: { disapproval: input.disapproval },
      });
      return res.data ?? null;
    } catch (reason) {
      let message = "Unknown error!";
      if (isApiError(reason)) {
        const err = toJspApiError(reason);
        message = `[${err.status}] ${err.statusText}`;
        if (isErrorResponseBodyType(err.body)) {
          message = err.body.detail;
          if (err.status === 422) {
            message = JSON.stringify(err.body.detail);
          }
        }
      }
      return rejectWithValue(message);
    }
  },
);

const updateFoodItemDetail = (
  state: FoodItemDetailStateProps,
  action: PayloadAction<
    | FoodItem
    | FoodItemAndEquivalentFoods
    | FoodItemAndVariantFoods
    | null
    | undefined
  >,
): FoodItemDetailStateProps => {
  const payload = action.payload ?? null;
  const {
    food_tags: foodTags,
    food_nutrition_records: foodNutritionRecords,
    food_recipes: foodRecipes,
    food_suppliers: foodSuppliers,
    equivalent_food_items: equivalentFoodItemsField,
    variant_food_items: variantFoodItemsField,
    ...foodItemFull
  } = (payload ?? { food_tags: initialState.foodTags }) as FoodItem & {
    equivalent_food_items?: FoodItem[];
    variant_food_items?: FoodItem[];
  };
  return {
    ...state,
    foodItemFull: isEmpty(foodItemFull) ? null : (foodItemFull as FoodItem),
    foodItem: isEmpty(foodItemFull) ? null : (foodItemFull as FoodItemBasic),
    foodTags: foodTags ?? [],
    foodNutritionRecords: foodNutritionRecords ?? [],
    foodRecipes: foodRecipes ?? [],
    foodSuppliers: foodSuppliers ?? [],
    equivalentFoodItems:
      equivalentFoodItemsField ??
      (payload === null ? [] : state.equivalentFoodItems),
    variantFoodItems:
      variantFoodItemsField ?? (payload === null ? [] : state.variantFoodItems),
    usedAsIngredientIn: payload === null ? [] : state.usedAsIngredientIn,
    usedInMenus: payload === null ? [] : state.usedInMenus,
    usedInMenuLoops: payload === null ? [] : state.usedInMenuLoops,
  };
};

export const foodItemDetailSlice = createSlice({
  name: "foodItemDetailSlice",
  initialState,
  reducers: {
    setFoodItemDetail: updateFoodItemDetail,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFoodItemDetail.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchFoodItemDetail.fulfilled, (state, action) => {
      return {
        ...state,
        ...updateFoodItemDetail(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchFoodItemDetail.rejected, (state, action) => {
      return { ...state, isLoading: false, error: action.payload as string };
    });
    builder.addCase(fetchFoodItemAndVariants.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchFoodItemAndVariants.fulfilled, (state, action) => {
      return {
        ...state,
        ...updateFoodItemDetail(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchFoodItemAndVariants.rejected, (state, action) => {
      return { ...state, isLoading: false, error: action.payload as string };
    });
    builder.addCase(fetchFoodItemUsages.fulfilled, (state, action) => {
      const { payload } = action;
      return {
        ...state,
        usedAsIngredientIn: payload?.used_as_ingredient_in ?? [],
        usedInMenus: payload?.used_in_menus ?? [],
        usedInMenuLoops: payload?.used_in_menu_loops ?? [],
      };
    });
    builder.addCase(fetchFoodItemUsages.rejected, (state, action) => {
      return { ...state, error: action.payload as string };
    });
    builder.addCase(updateFoodItemApprovalState.pending, (state) => {
      return { ...state, isApprovalChanging: true };
    });
    builder.addCase(updateFoodItemApprovalState.fulfilled, (state, action) => {
      return {
        ...state,
        ...updateFoodItemDetail(state, action),
        isApprovalChanging: false,
      };
    });
    builder.addCase(updateFoodItemApprovalState.rejected, (state, action) => {
      return {
        ...state,
        isApprovalChanging: false,
        error: action.payload as string,
      };
    });
  },
});

export const actions = {
  ...foodItemDetailSlice.actions,
  fetchFoodItemDetail,
  fetchFoodItemUsages,
  fetchFoodItemAndVariants,
  updateFoodItemApprovalState,
};

export const foodItemDetailReducer = foodItemDetailSlice.reducer;

export default foodItemDetailReducer;
